import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  User, Layers, Banknote, ShoppingBag, ShieldCheck, CreditCard,
  Bell, Lock, Database, LifeBuoy, Scale, LogOut, ChevronRight, RotateCcw,
} from "lucide-react";
import { useAppState } from "@/context/StateContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    title: "Account",
    items: [
      { id: "personal-information", label: "Personal Information", desc: "Name, contact and profile details", icon: User },
      { id: "financial-foundation", label: "Financial Foundation", desc: "How your money plan is set up", icon: Layers },
      { id: "income-settings", label: "Income Settings", desc: "When and how money comes in", icon: Banknote },
      { id: "living-bucket", label: "Living Bucket", desc: "Your everyday spending money", icon: ShoppingBag },
      { id: "safety-cushion", label: "Safety Cushion", desc: "Money kept safe for emergencies", icon: ShieldCheck },
      { id: "debt-information", label: "Debt Information", desc: "Your accounts and balances", icon: CreditCard },
    ],
  },
  {
    title: "Preferences",
    items: [
      { id: "notifications", label: "Notifications", desc: "Reminders and updates", icon: Bell },
      { id: "security", label: "Security", desc: "Password and sign-in", icon: Lock },
      { id: "privacy-data", label: "Privacy & Data", desc: "How your data is used", icon: Database },
    ],
  },
  {
    title: "Support",
    items: [
      { id: "help-support", label: "Help & Support", desc: "Get answers and contact us", icon: LifeBuoy },
      { id: "legal", label: "Legal", desc: "Terms and disclosures", icon: Scale },
    ],
  },
];

function SettingsRow({ item, onClick }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      data-testid={`settings-${item.id}`}
      onClick={onClick}
      className="flex min-h-[44px] w-full items-center gap-3.5 px-5 py-4 text-left transition-colors hover:bg-[#F5F2EC]"
    >
      <span className="flex min-h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F5F2EC] text-[#2F6B57]">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-[#171717]">{item.label}</span>
        <span className="block truncate text-sm text-[#70584A]">{item.desc}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-[#A89A88]" />
    </button>
  );
}

export default function Profile() {
  const { state, reset } = useAppState();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!state) return null;
  const { customer } = state;

  const comingSoon = (label) => toast(`${label} is coming soon.`);
  const doSignOut = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/login", { replace: true });
  };
  const doReset = async () => {
    setBusy(true);
    try {
      await reset();
      toast.success("Demo reset to starting state.");
      navigate("/app");
    } catch (e) {
      toast.error("Could not reset. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up space-y-6" data-testid="profile-view">
      <header>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DDE7DF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#164C3B]">
          <User className="h-3.5 w-3.5" /> Profile
        </span>
        <h1 className="mt-3 font-serif text-3xl text-[#171717] sm:text-4xl">Hello, {customer?.first_name}</h1>
      </header>

      {/* Identity */}
      <div className="flex items-center gap-4 rounded-2xl pe-surface p-6" data-testid="profile-identity-card">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#164C3B] font-serif text-2xl text-white">
          {customer?.first_name?.[0]}
        </span>
        <div>
          <p className="font-serif text-2xl text-[#171717]">{customer?.first_name}</p>
          <p className="text-sm text-[#70584A]">The Paycheck Edit demo customer</p>
        </div>
      </div>

      {/* Settings sections */}
      {SECTIONS.map((section) => (
        <div key={section.title} data-testid={`settings-section-${section.title.toLowerCase()}`}>
          <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#70584A]">
            {section.title}
          </p>
          <div className="divide-y divide-[#E5E0D9] overflow-hidden rounded-2xl pe-surface">
            {section.items.map((item) => (
              <SettingsRow
                key={item.id}
                item={item}
                onClick={() =>
                  item.id === "financial-foundation" ? navigate("/foundation") : comingSoon(item.label)
                }
              />
            ))}
          </div>
        </div>
      ))}

      {/* Sign out */}
      <button
        type="button"
        data-testid="sign-out-button"
        onClick={doSignOut}
        className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl pe-surface px-5 py-4 font-medium text-[#991B1B] transition-colors hover:bg-[#F5F2EC]"
      >
        <LogOut className="h-4 w-4" />
        Sign Out
      </button>

      {/* Prototype controls (existing demo reset) */}
      <div className="rounded-2xl pe-surface p-6" data-testid="prototype-controls">
        <p className="font-serif text-xl text-[#164C3B]">Prototype controls</p>
        <p className="mt-1.5 text-sm text-[#525252]">Reset the demo back to Jordan's starting balances.</p>
        <Button
          variant="outline"
          data-testid="reset-demo-button"
          onClick={doReset}
          disabled={busy}
          className="mt-4 min-h-11 rounded-xl border-[#E5E0D9] bg-white font-medium text-[#70584A] hover:bg-[#F5F2EC]"
        >
          <RotateCcw className="mr-1.5 h-4 w-4" />
          {busy ? "Resetting…" : "Reset demo data"}
        </Button>
      </div>
    </div>
  );
}
